import { useMemo, useCallback } from 'react'

import {
  getLabelFromStorage,
  getFieldsInOrderBy,
  getFieldsBy,
  FieldConfig as FieldConfigUtils,
} from '@pb/utils'
import { reject, isEmpty, toPairs, map, mergeDeepRight } from 'ramda'
import { useSelector } from 'react-redux'

import appSelectors from 'App/duck/selectors'
import { FieldConfig, DateFieldConfig } from 'App/duck/types'
import checkoutSelectors from 'features/checkout/duck/selectors'

import {
  ModifiedFieldConfig,
  ModifiedSelectFieldConfig,
  Overrides,
  SelectOption,
  UseFieldConfigsReturnType,
} from './types'

const useFieldsConfigs = (
  fieldGroup: string,
  overrides: Overrides = {},
): UseFieldConfigsReturnType => {
  const allFieldsConfig = useSelector(appSelectors.getFieldsConfig) as FieldConfigUtils[]
  const fieldsOrdering = useSelector(appSelectors.getFieldsOrdering)
  const titles = useSelector(appSelectors.getTitles)
  const provinces = useSelector(appSelectors.getProvinces)
  const allFieldsInGroup: FieldConfig[] = getFieldsBy(fieldGroup, allFieldsConfig)
  const isGeoBlockingDisabled = useSelector(appSelectors.getIsGeoBlockingDisabled)
  const geoBlockingCountries = useSelector(appSelectors.getGeoBlockingCountries)
  const billingCountries = useSelector(appSelectors.getBillingCountries)
  const countries = (isGeoBlockingDisabled ? geoBlockingCountries : billingCountries) || []
  const cities = useSelector(checkoutSelectors.getCities)
  const districts = useSelector(checkoutSelectors.getDistricts)

  const selectOptions = {
    title: titles ? map(([value, label]) => ({ value, label }), toPairs(titles)) : [],
    country: map((field) => ({ value: field.isocode, label: field.name }), countries),
    province: [
      {
        value: '',
        label: getLabelFromStorage('checkout.externaladdress.province.placeholder.text'),
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ...map(([, label]) => ({ value: label, label }), toPairs(provinces)),
    ],
    town: [
      {
        value: '',
        label: getLabelFromStorage('town.placeholder'),
      },
      ...map(([, label]) => ({ value: label, label }), toPairs(cities)),
    ],
    district: [
      {
        value: '',
        label: getLabelFromStorage('district.placeholder'),
      },
      ...map(([, label]) => ({ value: label, label }), toPairs(districts)),
    ],
  }

  const fieldsConfig = useMemo(
    () =>
      reject(isEmpty, allFieldsInGroup).flatMap((fieldConfig: FieldConfig) => {
        let modifiedFieldConfig: ModifiedFieldConfig | ModifiedFieldConfig[] = { ...fieldConfig }
        const {
          attribute,
          fieldType: { code: fieldTypeCode, name: fieldTypeName },
        } = fieldConfig

        if (fieldTypeCode === 'DROPDOWN' || fieldTypeCode === 'COMBOBOX') {
          modifiedFieldConfig = modifiedFieldConfig as ModifiedSelectFieldConfig

          // Populate select options by attribute
          const fieldSelectOptions: SelectOption[] = selectOptions[attribute]
          if (fieldSelectOptions) {
            modifiedFieldConfig.selectOptions = fieldSelectOptions
          }

          // It is also possible to receive selectOptions from the fieldsConfig response.
          if ('value' in fieldConfig && !!fieldConfig.value) {
            const { value: selectValues } = fieldConfig

            modifiedFieldConfig.selectOptions = map(
              ([value, label]) => ({ value, label }),
              toPairs(selectValues),
            )
          }
        }

        const fieldConfigOverrides = overrides[attribute]
        if (fieldConfigOverrides) {
          modifiedFieldConfig = mergeDeepRight(modifiedFieldConfig, fieldConfigOverrides)
        }

        if (fieldTypeName === 'DATE') {
          const dateFields = getFieldsInOrderBy(
            'DATE',
            allFieldsConfig,
            fieldsOrdering,
          ) as DateFieldConfig[]

          modifiedFieldConfig = [modifiedFieldConfig, ...dateFields]
        }

        return modifiedFieldConfig
      }),
    [allFieldsInGroup, selectOptions],
  ) as FieldConfigUtils[]

  const fieldsInOrder = useMemo(
    () => getFieldsInOrderBy(fieldGroup, fieldsConfig, fieldsOrdering),
    [fieldGroup, fieldsConfig, fieldsOrdering],
  )

  const dateFields = useMemo(
    () => getFieldsInOrderBy('DATE', fieldsConfig, fieldsOrdering) as DateFieldConfig[],
    [fieldsConfig, fieldsOrdering],
  )

  const getFieldByAttribute = useCallback(
    (attribute: string): FieldConfig | undefined =>
      fieldsConfig.find((f) => f.attribute === attribute),
    [fieldsConfig],
  )

  return { fieldsInOrder, getFieldByAttribute, dateFields }
}

export default useFieldsConfigs
